import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Star, CheckSquare, Square, ArchiveRestore, Archive, Award, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { neonQuery } from '../services/api';

const PAGE_SIZE = 20;

function thumbUrl(key) {
  if (!key) return '';
  return `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
}

export default function MediaLibraryDedupe() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map((r) => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('Manager') || userRoles.includes('Marketing');

  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalClusters, setTotalClusters] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // variantId → boolean (true = will archive)
  const [selections, setSelections] = useState({});
  const [busyCluster, setBusyCluster] = useState(null);
  // { clusterId, index } — which asset inside which cluster the user is zooming.
  const [zoom, setZoom] = useState(null);

  const load = useCallback(async (nextOffset) => {
    setLoading(true); setError(null);
    try {
      const res = await neonQuery('media_assets_clusters_list', { limit: PAGE_SIZE, offset: nextOffset });
      const list = Array.isArray(res) ? res : (res?.rows || []);
      // total_clusters is attached to every row via a window function in the
      // handler; pull it off the first row on this page.
      const total = list[0]?.total_clusters ? parseInt(list[0].total_clusters, 10) : 0;
      setRows(list);
      setTotalClusters(total);
      // Default every variant's checkbox to ON (archive) — user unchecks the
      // ones they want to keep. Faster than the alternative.
      const next = {};
      for (const r of list) if (r.cluster_role === 'variant') next[r.id] = true;
      setSelections(next);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAllowed) load(offset); }, [isAllowed, offset, load]);

  const clusters = useMemo(() => {
    const byId = new Map();
    for (const row of rows) {
      if (!byId.has(row.cluster_id)) byId.set(row.cluster_id, { clusterId: row.cluster_id, primary: null, variants: [] });
      const c = byId.get(row.cluster_id);
      if (row.cluster_role === 'primary') c.primary = row;
      else c.variants.push(row);
    }
    return [...byId.values()].filter((c) => c.primary && c.variants.length > 0);
  }, [rows]);

  if (!isAllowed) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Admin or Manager role required.</div>;

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalClusters / PAGE_SIZE));

  const toggle = (id) => setSelections((s) => ({ ...s, [id]: !s[id] }));

  const archiveSelected = async (cluster) => {
    const ids = cluster.variants.filter((v) => selections[v.id]).map((v) => v.id);
    if (!ids.length) return;
    setBusyCluster(cluster.clusterId);
    try {
      await neonQuery('media_assets_cluster_review', {
        action: 'archive', clusterId: cluster.clusterId, variantIds: ids, admin_id: user?.id,
      });
      // Drop archived rows locally — if that empties the cluster of active
      // variants, it disappears from the list on next fetch, but for now just
      // filter client-side so the UI reflects the change immediately.
      setRows((prev) => prev.filter((r) => !ids.includes(r.id)));
    } catch (e) { setError(`Archive failed: ${e.message}`); }
    finally { setBusyCluster(null); }
  };

  const unmarkCluster = async (cluster) => {
    if (!window.confirm(`Clear cluster ${cluster.clusterId}? All ${cluster.variants.length + 1} images stay active and become independent.`)) return;
    setBusyCluster(cluster.clusterId);
    try {
      await neonQuery('media_assets_cluster_review', {
        action: 'unmark', clusterId: cluster.clusterId, admin_id: user?.id,
      });
      setRows((prev) => prev.filter((r) => r.cluster_id !== cluster.clusterId));
    } catch (e) { setError(`Unmark failed: ${e.message}`); }
    finally { setBusyCluster(null); }
  };

  const promote = async (cluster, variantId) => {
    setBusyCluster(cluster.clusterId);
    try {
      await neonQuery('media_assets_cluster_review', {
        action: 'promote', clusterId: cluster.clusterId, promoteId: variantId, admin_id: user?.id,
      });
      // Swap roles locally
      setRows((prev) => prev.map((r) => {
        if (r.cluster_id !== cluster.clusterId) return r;
        if (r.id === variantId) return { ...r, cluster_role: 'primary' };
        if (r.cluster_role === 'primary') return { ...r, cluster_role: 'variant' };
        return r;
      }));
    } catch (e) { setError(`Promote failed: ${e.message}`); }
    finally { setBusyCluster(null); }
  };

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, borderBottom: '1px solid var(--gray-200)', paddingBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: 'var(--navy)' }}>Media Library · Dedupe Review</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 13 }}>
            {totalClusters} clusters awaiting review. Uncheck variants you want to keep, then "Archive selected."
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0 || loading}
            style={pageBtn(offset === 0)}>← Prev</button>
          <span style={{ color: 'var(--gray-500)' }}>page {page} / {totalPages}</span>
          <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={page >= totalPages || loading}
            style={pageBtn(page >= totalPages)}>Next →</button>
        </div>
      </div>

      {error ? <div style={{ background: '#fff0f0', border: '1px solid #ffcccc', color: '#a00000', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div> : null}
      {loading ? <div style={{ color: 'var(--gray-500)', padding: 20, display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={16} className="spin" /> Loading clusters...</div> : null}
      {!loading && clusters.length === 0 ? <div style={{ color: 'var(--gray-500)', padding: 40, textAlign: 'center' }}>No clusters awaiting review on this page.</div> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {clusters.map((c) => (
          <ClusterRow
            key={c.clusterId}
            cluster={c}
            selections={selections}
            onToggle={toggle}
            onArchive={() => archiveSelected(c)}
            onUnmark={() => unmarkCluster(c)}
            onPromote={(vid) => promote(c, vid)}
            onZoom={(index) => setZoom({ clusterId: c.clusterId, index })}
            busy={busyCluster === c.clusterId}
          />
        ))}
      </div>

      {zoom ? (
        <ZoomModal
          cluster={clusters.find((c) => c.clusterId === zoom.clusterId)}
          index={zoom.index}
          onNav={(delta) => setZoom((z) => {
            const cluster = clusters.find((c) => c.clusterId === z.clusterId);
            const order = [cluster.primary, ...cluster.variants];
            const next = (z.index + delta + order.length) % order.length;
            return { ...z, index: next };
          })}
          onClose={() => setZoom(null)}
        />
      ) : null}
    </div>
  );
}

function ClusterRow({ cluster, selections, onToggle, onArchive, onUnmark, onPromote, onZoom, busy }) {
  const selectedCount = cluster.variants.filter((v) => selections[v.id]).length;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, opacity: busy ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Primary</div>
          <AssetTile asset={cluster.primary} role="primary" onZoom={() => onZoom(0)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
            <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
              Variants ({cluster.variants.length}) — {selectedCount} selected for archive
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>cluster #{cluster.clusterId}</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {cluster.variants.map((v, i) => (
              <VariantTile
                key={v.id}
                asset={v}
                selected={!!selections[v.id]}
                onToggle={() => onToggle(v.id)}
                onPromote={() => onPromote(v.id)}
                onZoom={() => onZoom(i + 1)}
              />
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={onArchive} disabled={selectedCount === 0 || busy}
              style={actionBtn(selectedCount === 0, '#c47000', '#fff')}>
              <Archive size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              Archive selected ({selectedCount})
            </button>
            <button onClick={onUnmark} disabled={busy} style={actionBtn(false, 'transparent', 'var(--gray-500)', '1px solid var(--gray-200)')}>
              <ArchiveRestore size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              Not a dupe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetTile({ asset, role, onZoom }) {
  return (
    <div style={{ width: 180, background: '#f0f0f3', borderRadius: 6, overflow: 'hidden', position: 'relative', border: role === 'primary' ? '2px solid var(--orange)' : '1px solid var(--gray-200)' }}>
      <div style={{ aspectRatio: '1 / 1', position: 'relative', cursor: onZoom ? 'zoom-in' : 'default' }} onClick={onZoom}>
        <img src={thumbUrl(asset.r2_thumb_key)} alt="" loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        <DimsBadge asset={asset} />
        <QualityBadge q={asset.quality_score} />
        {asset.human_starred ? (
          <div style={{ position: 'absolute', top: 6, right: 6, background: '#ffd60a', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Star size={12} fill="#1d1d1f" color="#1d1d1f" />
          </div>
        ) : null}
      </div>
      <div style={{ padding: '6px 8px', fontSize: 10 }}>
        <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{asset.category} / {asset.subcategory}</div>
        <div style={{ color: 'var(--gray-500)', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 2 }}>{asset.suggested_name}</div>
      </div>
    </div>
  );
}

function VariantTile({ asset, selected, onToggle, onPromote, onZoom }) {
  return (
    <div style={{ position: 'relative', width: 140 }}>
      <div style={{ background: '#f0f0f3', borderRadius: 6, overflow: 'hidden', border: selected ? '2px solid #c47000' : '2px solid transparent', opacity: selected ? 1 : 0.6 }}>
        <div style={{ aspectRatio: '1 / 1', position: 'relative', cursor: 'zoom-in' }} onClick={onZoom}>
          <img src={thumbUrl(asset.r2_thumb_key)} alt="" loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <DimsBadge asset={asset} />
          <QualityBadge q={asset.quality_score} />
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title={selected ? 'Unselect (keep active)' : 'Select for archive'}
            style={{ position: 'absolute', top: 4, right: 4, background: '#fff', borderRadius: 4, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {selected ? <CheckSquare size={14} color="#c47000" /> : <Square size={14} color="var(--gray-500)" />}
          </button>
          {asset.human_starred ? (
            <div style={{ position: 'absolute', top: 4, left: 4, background: '#ffd60a', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Star size={11} fill="#1d1d1f" color="#1d1d1f" />
            </div>
          ) : null}
        </div>
      </div>
      <button onClick={onPromote}
        title="Promote this variant to primary"
        style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 12, padding: '2px 8px', fontSize: 10, color: 'var(--navy)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
        <Award size={10} /> promote
      </button>
    </div>
  );
}

function ZoomModal({ cluster, index, onNav, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onNav(-1);
      else if (e.key === 'ArrowRight') onNav(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNav]);
  if (!cluster) return null;
  const order = [cluster.primary, ...cluster.variants];
  const asset = order[index];
  const isPrimary = index === 0;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
      >
        {asset.media_kind === 'video' && asset.r2_original_key ? (
          <video
            src={`/.netlify/functions/r2-fetch?key=${encodeURIComponent(asset.r2_original_key)}`}
            poster={thumbUrl(asset.r2_thumb_key)}
            controls
            preload="metadata"
            style={{ maxWidth: '90vw', maxHeight: '78vh', background: '#222', borderRadius: 6, boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}
          />
        ) : (
          <img
            src={thumbUrl(asset.r2_thumb_key)}
            alt={asset.alt_text || ''}
            style={{ maxWidth: '90vw', maxHeight: '78vh', objectFit: 'contain', background: '#222', borderRadius: 6, boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}
          />
        )}
        <div style={{ color: '#fff', fontSize: 13, display: 'flex', gap: 14, alignItems: 'center', background: 'rgba(0,0,0,0.6)', padding: '8px 14px', borderRadius: 6 }}>
          <span style={{ background: isPrimary ? 'var(--orange)' : '#555', color: '#fff', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            {isPrimary ? 'Primary' : `Variant ${index}/${cluster.variants.length}`}
          </span>
          <span style={{ fontFamily: 'monospace' }}>{asset.width}×{asset.height}</span>
          <span>q{asset.quality_score}</span>
          <span style={{ color: '#ccc' }}>{asset.category}/{asset.subcategory}</span>
          <span style={{ color: '#aaa', fontFamily: 'monospace', fontSize: 11 }}>{asset.suggested_name}</span>
        </div>
      </div>

      {order.length > 1 ? (
        <>
          <button onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft size={28} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(1); }}
            style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronRight size={28} />
          </button>
        </>
      ) : null}
      <button onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={22} />
      </button>
    </div>
  );
}

function DimsBadge({ asset }) {
  if (!asset.width || !asset.height) return null;
  return (
    <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '1px 5px', borderRadius: 3, fontSize: 9, fontFamily: 'monospace' }}>
      {asset.width}×{asset.height}
    </div>
  );
}

function QualityBadge({ q }) {
  const score = q || 0;
  return (
    <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'var(--navy)', padding: '2px 5px', borderRadius: 3, fontSize: 10, fontFamily: 'monospace', lineHeight: 1 }}>
      <span style={{ color: 'var(--orange)' }}>{'★'.repeat(score)}</span>
      <span style={{ color: 'rgba(255,255,255,0.35)' }}>{'★'.repeat(5 - score)}</span>
    </div>
  );
}

const pageBtn = (disabled) => ({
  padding: '6px 10px', fontSize: 12, background: '#fff', border: '1px solid var(--gray-200)',
  borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? 'var(--gray-500)' : 'var(--navy)',
});
const actionBtn = (disabled, bg, color, border = 'none') => ({
  padding: '7px 14px', fontSize: 12, background: disabled ? '#eee' : bg, color: disabled ? 'var(--gray-500)' : color,
  border, borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 500,
});
